import React, { useEffect, useState, useRef } from 'react';
import { Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useLanguage } from '../../../i18n/LanguageContext';

// Resizable Image React Component with visual handles
export const ResizableImageComponent = (props: any) => {
  // TipTap mounts this node view itself, so it sits outside the editor's prop
  // tree — the language pack has to come from the context here.
  const { t } = useLanguage();
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef({ x: 0, y: 0, width: 0, height: 0, angle: 0 });
  
  // Initialize dimensions from props or natural image size
  const [dimensions, setDimensions] = useState(() => {
    return {
      width: props.node.attrs.width ? parseInt(props.node.attrs.width) : null,
      height: props.node.attrs.height ? parseInt(props.node.attrs.height) : null,
    };
  });

  // Log selection changes
  // Set initial dimensions when image loads
  useEffect(() => {
    const img = imgRef.current;
    const { updateAttributes } = props;
    
    if (img && !dimensions.width && !dimensions.height) {
      const onLoad = () => {
        const newDimensions = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
        setDimensions(newDimensions);
        // Also save to node attributes so they persist
        updateAttributes({
          width: newDimensions.width.toString(),
          height: newDimensions.height.toString(),
        });
      };
      
      if (img.complete) {
        onLoad();
      } else {
        img.addEventListener('load', onLoad);
        return () => img.removeEventListener('load', onLoad);
      }
    }
  }, [dimensions.width, dimensions.height, props]);

  const handleResizeMouseDown = (e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const container = containerRef.current;
    if (!container) return;
    
    // Get current dimensions and position from the container
    const rect = container.getBoundingClientRect();
    
    startPosRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: rect.width,
      height: rect.height,
      angle: 0,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startPosRef.current.x;
      const deltaY = moveEvent.clientY - startPosRef.current.y;
      let newWidth = startPosRef.current.width;
      let newHeight = startPosRef.current.height;
      
      // Side handles - only affect width, keep height unchanged
      if (handle === 'right') {
        newWidth = Math.max(100, Math.min(1200, startPosRef.current.width + deltaX));
        newHeight = startPosRef.current.height;
      } else if (handle === 'left') {
        const proposedWidth = startPosRef.current.width - deltaX;
        newWidth = Math.max(100, Math.min(1200, proposedWidth));
        newHeight = startPosRef.current.height;
      }
      // Top/bottom handles - only affect height, keep width unchanged
      else if (handle === 'bottom') {
        newHeight = Math.max(50, Math.min(1200, startPosRef.current.height + deltaY));
        newWidth = startPosRef.current.width;
      } else if (handle === 'top') {
        const proposedHeight = startPosRef.current.height - deltaY;
        newHeight = Math.max(50, Math.min(1200, proposedHeight));
        newWidth = startPosRef.current.width;
      }
      // Corner handles - maintain aspect ratio while resizing
      else if (handle === 'top-right' || handle === 'bottom-right' || handle === 'bottom-left' || handle === 'top-left') {
        const aspectRatio = startPosRef.current.width / startPosRef.current.height;
        
        if (handle === 'top-right') {
          const proposedWidth = startPosRef.current.width + deltaX;
          newWidth = Math.max(100, Math.min(1200, proposedWidth));
          newHeight = newWidth / aspectRatio;
        } else if (handle === 'bottom-right') {
          const proposedWidth = startPosRef.current.width + deltaX;
          newWidth = Math.max(100, Math.min(1200, proposedWidth));
          newHeight = newWidth / aspectRatio;
        } else if (handle === 'top-left') {
          const proposedWidth = startPosRef.current.width - deltaX;
          newWidth = Math.max(100, Math.min(1200, proposedWidth));
          newHeight = newWidth / aspectRatio;
        } else if (handle === 'bottom-left') {
          const proposedWidth = startPosRef.current.width - deltaX;
          newWidth = Math.max(100, Math.min(1200, proposedWidth));
          newHeight = newWidth / aspectRatio;
        }
      }
      
      setDimensions({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      props.updateAttributes({
        width: dimensions.width ? Math.round(dimensions.width).toString() : null,
        height: dimensions.height ? Math.round(dimensions.height).toString() : null,
      });
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleRotateMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRotating(true);
    
    const container = containerRef.current;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const currentRotation = props.node.attrs.rotation || 0;
    startPosRef.current = {
      x: centerX,
      y: centerY,
      width: 0,
      height: 0,
      angle: currentRotation,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startPosRef.current.x;
      const deltaY = moveEvent.clientY - startPosRef.current.y;
      
      // Calculate angle from center
      let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
      angle = (angle + 90) % 360;
      if (angle < 0) angle += 360;
      
      props.updateAttributes({ rotation: Math.round(angle) });
    };

    const handleMouseUp = () => {
      setIsRotating(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const rotation = props.node.attrs.rotation || 0;
  const alignment = props.node.attrs.alignment || 'left';

  const setImageAlignment = (align: 'left' | 'center' | 'right') => {
    props.updateAttributes({ alignment: align });
  };

  return (
    <NodeViewWrapper 
      className="resizable-image-wrapper" 
      data-alignment={alignment}
    >
      <div 
        ref={containerRef}
        className={`image-container ${props.selected ? 'selected' : ''}`}
        style={{
          width: dimensions.width ? `${dimensions.width}px` : 'auto',
          height: dimensions.height ? `${dimensions.height}px` : 'auto',
        }}
      >
        <img
          ref={imgRef}
          src={props.node.attrs.src}
          alt={props.node.attrs.alt || ''}
          title={props.node.attrs.title || ''}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'fill',
            transform: `rotate(${rotation}deg)`,
            transition: isResizing || isRotating ? 'none' : 'transform 0.2s',
            display: 'block',
            margin: 0,
            padding: 0,
          }}
        />
        
        {props.selected && (
          <>
            {/* Alignment controls */}
            <div className="image-alignment-controls">
              <button
                className={`alignment-btn ${alignment === 'left' ? 'active' : ''}`}
                onClick={() => setImageAlignment('left')}
                title={t.editor.alignLeft}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h18v2H3V3zm0 4h12v2H3V7zm0 4h18v2H3v-2zm0 4h12v2H3v-2zm0 4h18v2H3v-2z"/>
                </svg>
              </button>
              <button
                className={`alignment-btn ${alignment === 'center' ? 'active' : ''}`}
                onClick={() => setImageAlignment('center')}
                title={t.editor.alignCenter}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h18v2H3V3zm3 4h12v2H6V7zm-3 4h18v2H3v-2zm3 4h12v2H6v-2zm-3 4h18v2H3v-2z"/>
                </svg>
              </button>
              <button
                className={`alignment-btn ${alignment === 'right' ? 'active' : ''}`}
                onClick={() => setImageAlignment('right')}
                title={t.editor.alignRight}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h18v2H3V3zm6 4h12v2H9V7zm-6 4h18v2H3v-2zm6 4h12v2H9v-2zm-6 4h18v2H3v-2z"/>
                </svg>
              </button>
            </div>
            
            {/* Side resize handles */}
            <div
              className="resize-handle resize-handle-top"
              onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
            />
            <div
              className="resize-handle resize-handle-right"
              onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
            />
            <div
              className="resize-handle resize-handle-bottom"
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
            />
            <div
              className="resize-handle resize-handle-left"
              onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
            />
            
            {/* Corner resize handles */}
            <div
              className="resize-handle resize-handle-top-left"
              onMouseDown={(e) => handleResizeMouseDown(e, 'top-left')}
            />
            <div
              className="resize-handle resize-handle-top-right"
              onMouseDown={(e) => handleResizeMouseDown(e, 'top-right')}
            />
            <div
              className="resize-handle resize-handle-bottom-left"
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-left')}
            />
            <div
              className="resize-handle resize-handle-bottom-right"
              onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-right')}
            />
            
            {/* Rotation zones near corners */}
            <div
              className="rotate-zone rotate-zone-top-left"
              onMouseDown={handleRotateMouseDown}
            />
            <div
              className="rotate-zone rotate-zone-top-right"
              onMouseDown={handleRotateMouseDown}
            />
            <div
              className="rotate-zone rotate-zone-bottom-left"
              onMouseDown={handleRotateMouseDown}
            />
            <div
              className="rotate-zone rotate-zone-bottom-right"
              onMouseDown={handleRotateMouseDown}
            />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

// Custom Resizable Image extension
export const ResizableImage = Node.create({
  name: 'resizableImage',
  
  group: 'block',
  
  atom: true,
  
  draggable: true,
  
  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('width'),
        renderHTML: (attributes: any) => {
          if (!attributes.width) {
            return {};
          }
          return {
            width: attributes.width,
          };
        },
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('height'),
        renderHTML: (attributes: any) => {
          if (!attributes.height) {
            return {};
          }
          return {
            height: attributes.height,
          };
        },
      },
      alignment: {
        default: 'left',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-alignment') || 'left',
        renderHTML: (attributes: any) => {
          return {
            'data-alignment': attributes.alignment,
          };
        },
      },
      rotation: {
        default: 0,
        parseHTML: (element: HTMLElement) => parseInt(element.getAttribute('data-rotation') || '0'),
        renderHTML: (attributes: any) => {
          return {
            'data-rotation': attributes.rotation,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', HTMLAttributes];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },

  addCommands() {
    return {
      setImage: (options: { src: string; alt?: string; title?: string }) => ({ commands }: any) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});
